import { push } from 'connected-react-router'
import { call, put, select, takeEvery } from 'redux-saga/effects'

import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import type { IInstrumentId } from '@app/domain/instrument'
import type { IEntry } from '@app/domain/instrument/instrument.interface'
import type { IPatient } from '@app/domain/patient/patient.interface'
import type {
  IWorkflowStep,
  IScreen,
  IVisitDefinition,
  IVisit,
  IDataInstrument,
  IScreenQuestion} from '@app/domain/visit/visit.interface';
import {
  VisitStatusId,
  IWorkFlowStepType,
  VisitUseCase
} from '@app/domain/visit/visit.interface'
import type { AppAction, AppState } from '@app/state'
import * as VisitHelpers from '@app/state/visits/helpers'

import * as UserRequests from '../user/rest'
import * as Visits from '../visits/state'

import * as Helpers from './helpers'
import * as Requests from './rest'
import { VisitStage } from './state'
import * as Intake from './state'

import type { IVisitState} from './state';
import type { AxiosResponse } from 'axios'

function isWorkflowStepLogicValid(
  appState: AppState,
  definition: IVisitDefinition,
  workflowStepIndex: number,
) {
  if (
    definition.allWorkflow[workflowStepIndex]?.workflowLogic &&
    definition.allWorkflow[workflowStepIndex].workflowLogic !== ''
  ) {
    const context = Helpers.buildVisitContextWithResponses(
      appState.intake.visit,
      appState.intake.definition.allInstruments,
      appState.intake.currentState.entries,
      appState.intake.visit.patient,
    )

    const shouldShow = Helpers.evaluateConfiguredLogic(
      definition.allWorkflow[workflowStepIndex].workflowLogic,
      context,
    )
    if (shouldShow) {
      return true
    } else {
      return false
    }
  } else {
    return true
  }
}

function* spawnLoadTransitions() {
  yield takeEvery(Intake.ActionType.LoadVisit, loadVisit)
  yield takeEvery(Intake.ActionType.VisitRetrieved, loadEntries)
  yield takeEvery(Intake.ActionType.EntriesRetrieved, loaded)
  yield takeEvery(Intake.ActionType.LoadVisitSucceeded, resume)
}

function* loadVisit(action: AppAction) {
  if (action.type === Intake.ActionType.LoadVisit) {
    const appState: AppState = yield select((a) => a)
    yield put(Intake.actions.loadVisitPending())
    try {
      const request = Requests.getVisitById(action.visitId)
      const response: AxiosResponse<Requests.GetVisitResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const visit = VisitHelpers.convertApiVisit(
        response.data.visit.visit,
        response.data.visit.patient,
      )
      const defnRequest = Requests.retrieveVisitDefinition(action.visitId)
      const defnResponse: AxiosResponse<Requests.RetrieveVisitDefinitionResponse> = yield call(
        defnRequest,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const definition = VisitHelpers.convertApiVisitDefinition(
        defnResponse.data.definition,
      )
      yield put(Intake.actions.visitRetrieved(visit, definition))
      yield put(Intake.actions.updateVisitStatusOnLoad(visit.id, visit.status))

      if (visit.status !== VisitStatusId.Completed) {
        yield put(Intake.actions.updateVisitStatus(visit.id, 'in-progress'))
      }
    } catch (e) {
      yield put(Intake.actions.loadVisitFailed())
    }
  }
}

function* loadEntries() {
  yield put(Intake.actions.getEntries())
}

function* loaded() {
  yield put(Intake.actions.loadVisitSucceeded())
}

function* resumeIntakeWithValidWorkflowStep(currentState: IVisitState) {
  yield put(Intake.actions.resume(currentState))
}

function* resumeIntakeWithNewWorkflowStep(currentState: IVisitState) {
  const workflowStep = currentState.workflowStep + 1
  yield put(
    Intake.actions.resume({
      ...currentState,
      workflowStep,
    }),
  )
}

function* resumeSavedState(appState: AppState, currentState: IVisitState) {
  if (appState.intake.restartVisitFromBeginning) {
    yield put(
      Intake.actions.next({
        workflowStep: 0,
        screen: 0,
        stage: VisitStage.Collection,
        entries: currentState.entries,
        display: currentState.display,
        progress: 0.0,
      }),
    )
  } else {
    const savedVisitState = appState.intake.visit.state

    const sortedWorkflow = [...appState.intake.definition.allWorkflow].sort(
      (s1, s2) => s1.order - s2.order,
    )

    const workflowIdx = sortedWorkflow.findIndex(
      (v) => v.instrumentId === savedVisitState.instrumentId,
    )

    let completedScreens = 0

    completedScreens = Helpers.countCompletedScreens(
      appState.intake.definition.allInstruments,
      sortedWorkflow,
      workflowIdx,
      savedVisitState.screen,
    )

    yield put(
      Intake.actions.next({
        ...currentState,
        workflowStep: workflowIdx,
        screen: savedVisitState.screen,
        stage: (savedVisitState.stage as VisitStage) ?? VisitStage.Collection,
        progress: completedScreens,
      }),
    )
  }
}

function* handleRestart() {
  yield takeEvery(Intake.ActionType.Restart, resume)
}

function* resume() {
  const appState: AppState = yield select((a) => a)
  const { definition, currentState } = appState.intake
  const savedVisitState = appState.intake.visit.state

  if (
    !savedVisitState.instrumentId ||
    savedVisitState.stage === VisitStage.Completed
  ) {
    if (
      isWorkflowStepLogicValid(appState, definition, currentState.workflowStep)
    ) {
      yield* resumeIntakeWithValidWorkflowStep(currentState)
    } else if (currentState.workflowStep + 1 < definition.allWorkflow.length) {
      yield* resumeIntakeWithNewWorkflowStep(currentState)
    } else {
      const nextState = Helpers.determineNextStage(
        currentState,
        definition.isSignatureRequired,
        appState.featureFlags[FeatureFlagId.BypassPatientSignature],
      )
      yield put(Intake.actions.next(nextState))
    }
  } else {
    yield* resumeSavedState(appState, currentState)
  }
}

function* handleGetIntakeVisitEntries() {
  yield takeEvery(Intake.ActionType.GetEntries, getVisitEntries)
}

function* getVisitEntries(action: AppAction) {
  if (action.type === Intake.ActionType.GetEntries) {
    const appState: AppState = yield select((a) => a)
    try {
      const request = Requests.retrieveVisitEntries(appState.intake.visit.id)
      const response: AxiosResponse<Requests.RetrieveVisitEntriesResponse> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const entries = response.data.entries.map((apiEntry) =>
        Helpers.convertApiEntry(apiEntry),
      )
      yield put(Intake.actions.entriesRetrieved(entries))
    } catch (e) {
      yield put(Intake.actions.loadVisitFailed())
    }
  }
}

function* handleUpdateVisitStatus() {
  yield takeEvery(Intake.ActionType.UpdateVisitStatus, updateVisitStatus)
}

function* updateVisitStatus(action: AppAction) {
  if (action.type === Intake.ActionType.UpdateVisitStatus) {
    const appState: AppState = yield select((a) => a)
    try {
      const request = Requests.updateStatus(action.visitId, action.status)
      const response: AxiosResponse<Requests.UpdateStatusResult> = yield call(
        request,
        appState.config.apiLocation,
        appState.auth.token,
      )
      const visit = VisitHelpers.convertApiVisit(response.data.visit)
      yield put(Intake.actions.updateVisitStatusSuccess(visit))
    } catch (e) {
      yield put(
        Intake.actions.updateVisitStatusFailure(
          'unable to update visit status',
        ),
      )
    }
  }
}

function* handleSubmitResponses() {
  yield takeEvery(Intake.ActionType.SubmitResponses, submitResponses)
}

function* submitResponses(action: AppAction) {
  if (action.type === Intake.ActionType.SubmitResponses) {
    const appState: AppState = yield select((a) => a)
    const { entryId, responses, preventNextScreen } = action
    const request = Requests.submitResponses(entryId, responses)
    const response: AxiosResponse<Requests.SubmitResponsesResult> = yield call(
      request,
      appState.config.apiLocation,
      appState.auth.token,
    )
    yield put(
      Intake.actions.entryUpdated(Helpers.convertApiEntry(response.data.entry)),
    )
    if (!preventNextScreen) {
      yield put(Intake.actions.continue())
    }
  }
}

function* handleContinue() {
  yield takeEvery(Intake.ActionType.Continue, goToNextScreen)
}

function advanceScreen(
  screens: IScreen[],
  currentScreen: number,
  state: AppState,
) {
  const nextScreenIndex = currentScreen + 1

  // if we are at the last screen, end recursion
  if (nextScreenIndex === screens.length) {
    return -1
  } else {
    const nextScreen = screens[nextScreenIndex]
    if (Helpers.isScreenLogicValid(nextScreen) === true) {
      const context = Helpers.buildVisitContextWithResponses(
        state.intake.visit,
        state.intake.definition.allInstruments,
        state.intake.currentState.entries,
        state.intake.visit.patient,
      )

      const shouldShow = Helpers.evaluateConfiguredLogic(
        nextScreen.flowLogic,
        context,
      )
      if (shouldShow) {
        return nextScreenIndex
      } else {
        return advanceScreen(screens, nextScreenIndex, state)
      }
    } else {
      return nextScreenIndex
    }
  }
}

function retreatScreen(
  screens: IScreen[],
  currentScreen: number,
  state: AppState,
) {
  const prevScreenIndex = currentScreen - 1
  // if we are at the last screen, end recursion
  if (prevScreenIndex < 0) {
    return -1
  } else {
    const prevScreen = screens[prevScreenIndex]
    if (Helpers.isScreenLogicValid(prevScreen) === true) {
      const context = Helpers.buildVisitContextWithResponses(
        state.intake.visit,
        state.intake.definition.allInstruments,
        state.intake.currentState.entries,
        state.intake.visit.patient,
      )

      const shouldShow = Helpers.evaluateConfiguredLogic(
        prevScreen.flowLogic,
        context,
      )

      if (shouldShow) {
        return prevScreenIndex
      } else {
        return retreatScreen(screens, prevScreenIndex, state)
      }
    } else {
      return prevScreenIndex
    }
  }
}

function advanceInstrument(
  workflow: IWorkflowStep[],
  currentWorkflowStep: number,
  state: AppState,
) {
  const next = currentWorkflowStep + 1
  if (next === workflow.length) {
    return -1
  } else {
    const { definition } = state.intake
    if (isWorkflowStepLogicValid(state, definition, next)) {
      return next
    } else {
      return advanceInstrument(workflow, next, state)
    }
  }
}

function retreatInstrument(
  workflow: IWorkflowStep[],
  currentWorkflowStep: number,
  state: AppState,
) {
  const prev = currentWorkflowStep - 1
  if (prev < 0) {
    return -1
  } else {
    const { definition } = state.intake
    if (isWorkflowStepLogicValid(state, definition, prev)) {
      return prev
    } else {
      return retreatInstrument(workflow, prev, state)
    }
  }
}

function determineNextState(state, definition, currentState) {
  return Helpers.determineNextStage(
    currentState,
    definition.isSignatureRequired,
    state.featureFlags[FeatureFlagId.BypassPatientSignature],
  )
}

function getNextWorkflowOrStage(state, definition, currentState) {
  const nextWorkflowStep = advanceInstrument(
    definition.allWorkflow,
    currentState.workflowStep,
    state,
  )
  return nextWorkflowStep >= 0
    ? { ...currentState, workflowStep: nextWorkflowStep, screen: 0 }
    : determineNextState(state, definition, currentState)
}

function getNextScreenOrNextWorkflow(
  state,
  definition,
  currentState,
  intakeView,
) {
  const screens: IScreen[] = intakeView[0].screens
  const nextScreenIndex = advanceScreen(screens, currentState.screen, state)

  if (nextScreenIndex >= 0) {
    return {
      ...currentState,
      workflowStep: currentState.workflowStep,
      screen: nextScreenIndex,
    }
  }

  return getNextWorkflowOrStage(state, definition, currentState)
}

function* updateVisitStateWithNextState(state, definition, nextState) {
  yield put(
    Visits.actions.updateVisitState(state.intake.visit.id, {
      instrumentId: definition.allWorkflow[nextState.workflowStep].instrumentId,
      screen: nextState.screen,
      stage: nextState.stage,
    }),
  )
}

function* processNextForSignatureStage(state, definition, currentState) {
  const nextState = determineNextState(state, definition, currentState)
  yield updateVisitStateWithNextState(state, definition, nextState)
  yield put(Intake.actions.next(nextState))
}

function* processNextForDemographicConfirmation(
  state,
  definition,
  currentState,
) {
  const nextState = getNextWorkflowOrStage(state, definition, currentState)
  yield updateVisitStateWithNextState(state, definition, nextState)
  yield put(Intake.actions.next(nextState))
}

function* processNextIntakeView(state, definition, currentState, intakeView) {
  let nextState = getNextScreenOrNextWorkflow(
    state,
    definition,
    currentState,
    intakeView,
  )
  nextState = { ...nextState, progress: nextState.progress + 1.0 }
  yield updateVisitStateWithNextState(state, definition, nextState)
  yield put(Intake.actions.next(nextState))
}

function* goToNextScreen(action: AppAction) {
  if (action.type !== Intake.ActionType.Continue) {
    console.error('Missing intake view')
    return
  }

  const state: AppState = yield select((a) => a)
  const { definition, currentState } = state.intake

  if (currentState.stage === VisitStage.Signature) {
    yield processNextForSignatureStage(state, definition, currentState)
    return
  }

  const workflowStep = definition.allWorkflow[currentState.workflowStep]

  if (workflowStep.type === IWorkFlowStepType.DemographicConfirmation) {
    yield processNextForDemographicConfirmation(state, definition, currentState)
    return
  }

  const instrument = definition.allInstruments.find(
    (i) => i.id === workflowStep.instrumentId,
  )
  const intakeView = instrument.views.filter(
    (v) => v.useCase === VisitUseCase.Intake,
  )

  if (intakeView?.length === 1) {
    yield processNextIntakeView(state, definition, currentState, intakeView)
  }
}

function* handleGoBack() {
  yield takeEvery(Intake.ActionType.GoBack, goToPreviousScreen)
}

function retreatScreenBetweenInstrument(
  screens: IScreen[],
  startingScreenIndex: number,
  state: AppState,
): number {
  if (startingScreenIndex < 0 || startingScreenIndex >= screens.length) {
    return -1
  }

  const currentScreen = screens[startingScreenIndex]

  if (!currentScreen.flowLogic) {
    return startingScreenIndex
  }

  if (Helpers.isScreenLogicValid(currentScreen)) {
    const context = Helpers.buildVisitContextWithResponses(
      state.intake.visit,
      state.intake.definition.allInstruments,
      state.intake.currentState.entries,
      state.intake.visit.patient,
    )

    const shouldShow = Helpers.evaluateConfiguredLogic(
      currentScreen.flowLogic,
      context,
    )

    if (shouldShow) {
      return startingScreenIndex
    }
  }

  return retreatScreenBetweenInstrument(screens, startingScreenIndex - 1, state)
}

function getPreviousWorkflowOrInstrument(state, definition, currentState) {
  const previousWorkflowStepIndex = retreatInstrument(
    definition.allWorkflow,
    currentState.workflowStep,
    state,
  )

  if (previousWorkflowStepIndex >= 0) {
    const previousWorkflowStep =
      definition.allWorkflow[previousWorkflowStepIndex]
    if (
      previousWorkflowStep.type === IWorkFlowStepType.DemographicConfirmation
    ) {
      return {
        ...currentState,
        workflowStep: previousWorkflowStepIndex,
        screen: 0,
      }
    } else {
      const instrumentId = previousWorkflowStep.instrumentId
      const intakeView = definition.allInstruments
        .find((it) => it.id === instrumentId)
        ?.views.filter((v) => v.useCase === VisitUseCase.Intake)
      if (intakeView?.length === 1) {
        const screens = intakeView[0].screens
        const lastScreen = screens.length > 0 ? screens.length - 1 : 0

        if (!screens[lastScreen].flowLogic) {
          return {
            ...currentState,
            workflowStep: previousWorkflowStepIndex,
            screen: lastScreen,
          }
        }

        const validScreen = retreatScreenBetweenInstrument(
          screens,
          lastScreen,
          state,
        )

        if (validScreen >= 0) {
          return {
            ...currentState,
            workflowStep: previousWorkflowStepIndex,
            screen: validScreen,
          }
        } else {
          console.error(`No valid screen found for instrument: ${instrumentId}`)
        }
      } else {
        console.error(`No intake view on instrument: ${instrumentId}`)
      }
    }
  }

  return currentState
}

function getPreviousScreenOrWorkflow(
  state,
  definition,
  currentState,
  intakeView,
) {
  const screens: IScreen[] = intakeView[0].screens
  const prevScreenIndex = retreatScreen(screens, currentState.screen, state)

  if (prevScreenIndex >= 0) {
    return {
      ...currentState,
      workflowStep: currentState.workflowStep,
      screen: prevScreenIndex,
    }
  }

  if (currentState.workflowStep > 0) {
    return getPreviousWorkflowOrInstrument(state, definition, currentState)
  }

  return currentState
}

function* updateVisitStateForPreviousState(state, definition, prevState) {
  yield put(
    Visits.actions.updateVisitState(state.intake.visit.id, {
      instrumentId: definition.allWorkflow[prevState.workflowStep].instrumentId,
      screen: prevState.screen,
      stage: prevState.stage,
    }),
  )
}

function* processPreviousForCompletedStage(state, definition, currentState) {
  let prevState = {
    ...currentState,
    stage: VisitStage.Collection,
  }
  prevState = Helpers.reduceProgress(prevState)
  yield updateVisitStateForPreviousState(state, definition, prevState)
  yield put(Intake.actions.previous(prevState))
}

function* processPreviousForDemographicConfirmation(
  state,
  definition,
  currentState,
) {
  let prevState = getPreviousWorkflowOrInstrument(
    state,
    definition,
    currentState,
  )
  prevState = Helpers.reduceProgress(prevState)
  yield updateVisitStateForPreviousState(state, definition, prevState)
  yield put(Intake.actions.previous(prevState))
}

function* processPreviousForIntakeView(
  state,
  definition,
  currentState,
  intakeView,
) {
  let prevState = getPreviousScreenOrWorkflow(
    state,
    definition,
    currentState,
    intakeView,
  )
  prevState = Helpers.reduceProgress(prevState)
  yield updateVisitStateForPreviousState(state, definition, prevState)
  yield put(Intake.actions.previous(prevState))
}

function* goToPreviousScreen(action: AppAction) {
  if (action.type !== Intake.ActionType.GoBack) {
    return
  }

  const state: AppState = yield select((a) => a)
  const { definition, currentState } = state.intake

  if (currentState.stage === VisitStage.Completed) {
    yield processPreviousForCompletedStage(state, definition, currentState)
    return
  }

  const workflowStep = definition.allWorkflow[currentState.workflowStep]

  if (workflowStep.type === IWorkFlowStepType.DemographicConfirmation) {
    yield processPreviousForDemographicConfirmation(
      state,
      definition,
      currentState,
    )
    return
  }

  const instrument = definition.allInstruments.find(
    (i) => i.id === workflowStep.instrumentId,
  )
  const intakeView = instrument.views.filter(
    (v) => v.useCase === VisitUseCase.Intake,
  )

  if (intakeView?.length === 1) {
    yield processPreviousForIntakeView(
      state,
      definition,
      currentState,
      intakeView,
    )
  } else {
    console.error(`No intake view on instrument: ${instrument.id}`)
  }
}

function* handleEvaluateDisplayLogic() {
  yield takeEvery(Intake.ActionType.EvaluateDisplayLogic, evaluateDisplayLogic)
}

export function recordsAreEqual(
  a: Record<string, string | boolean>,
  b: Record<string, string | boolean>,
): boolean {
  if (a === b) {
    return true
  }

  if (typeof a !== typeof b) {
    return false
  }

  if (typeof a === 'object' && a !== null && b !== null) {
    const keysA = Object.keys(a)
    const keysB = Object.keys(b)
    if (keysA.length !== keysB.length) {
      return false
    }

    for (const key of keysA) {
      if (!keysB.includes(key)) {
        return false
      }
      if (a[key] !== b[key]) {
        return false
      }
    }

    return true
  }

  return false
}

export function updateEntry(
  entry: IEntry,
  updatedDisplayedScreenQuestions: Record<string, boolean>,
) {
  let responses = entry.responses
  Object.keys(updatedDisplayedScreenQuestions).forEach((questionId) => {
    if (updatedDisplayedScreenQuestions[questionId] === false) {
      responses = {
        ...responses,
        [questionId]: undefined,
      }
    }
  })

  return {
    ...entry,
    responses,
  }
}

export function updateEntries(
  instrumentId: IInstrumentId,
  entries: Record<IInstrumentId, IEntry>,
  updatedDisplayedScreenQuestions: Record<string, boolean>,
) {
  const entry = entries[instrumentId]
  if (entry !== undefined) {
    const updatedEntry = updateEntry(entry, updatedDisplayedScreenQuestions)
    return {
      ...entries,
      [instrumentId]: updatedEntry,
    }
  } else {
    return entries
  }
}

export function extractDisplayLogicQuestions(
  questions: IScreenQuestion[],
): IScreenQuestion[] {
  return questions.filter((q) => q.displayLogic && q.displayLogic !== '')
}

export function evaluateDisplayLogicForQuestions(
  questions: IScreenQuestion[],
  context: Record<string, unknown>,
): Record<string, boolean> {
  return questions.reduce<Record<string, boolean>>((acc, q) => {
    const shouldDisplay = Helpers.evaluateConfiguredLogic(
      q.displayLogic,
      context,
    )
    acc[q.questionId] = shouldDisplay
    return acc
  }, {})
}

export function updateVisitContext(
  visit: IVisit,
  instruments: IDataInstrument[],
  entries: Record<IInstrumentId, IEntry>,
  displayedQuestions: Record<string, boolean>,
  instrumentId: IInstrumentId,
  patient: IPatient,
): Record<string, unknown> {
  const updatedEntries = updateEntries(
    instrumentId,
    entries,
    displayedQuestions,
  )
  return Helpers.buildVisitContextWithResponses(
    visit,
    instruments,
    updatedEntries,
    patient,
  )
}

export function computeStableDisplayLogic(
  screen: IScreen,
  context: Record<string, unknown>,
  instrumentId: IInstrumentId,
  state: AppState,
  deleteHiddenResponsesMaximumInteractionCount: number,
): Record<string, boolean> {
  let current: Record<string, boolean> = {}
  let updated: Record<string, boolean> = {}
  let updatedContext = context
  let iteration = 0

  do {
    current = updated
    const logicQuestions = extractDisplayLogicQuestions(screen.questions)
    updated = evaluateDisplayLogicForQuestions(logicQuestions, updatedContext)

    updatedContext = updateVisitContext(
      state.intake.visit,
      state.intake.definition.allInstruments,
      state.intake.currentState.entries,
      updated,
      instrumentId,
      state.intake.visit.patient,
    )

    iteration++
  } while (
    !recordsAreEqual(current, updated) &&
    iteration < deleteHiddenResponsesMaximumInteractionCount
  )

  return updated
}

export function collectDisplayLogicResults(
  screens: IScreen[],
  context: Record<string, unknown>,
  instrumentId: IInstrumentId,
  state: AppState,
  deleteHiddenResponsesMaximumInteractionCount: number,
): Record<string, boolean> {
  let results = {}

  for (const screen of screens) {
    const displayMap = computeStableDisplayLogic(
      screen,
      context,
      instrumentId,
      state,
      deleteHiddenResponsesMaximumInteractionCount,
    )

    results = {
      ...results,
      ...displayMap,
    }
  }

  return results
}

export function* evaluateDisplayLogic(action: AppAction) {
  if (action.type === Intake.ActionType.EvaluateDisplayLogic) {
    const state: AppState = yield select((a) => a)
    const deleteHiddenResponsesMaximumInteractionCount =
      state.config.api.application.deleteHiddenResponsesMaximumInteractionCount

    const instrumentId = action.instrumentId
    const instrument = state.intake.definition.allInstruments.filter(
      (i) => i.id === instrumentId,
    )[0]

    const view = instrument.views.filter(
      (v) => v.useCase === VisitUseCase.Intake,
    )[0]
    const entry = state.intake.currentState.entries[instrumentId]

    if (action.questionId && action.response) {
      entry.responses[action.questionId] = action.response
    } else if (action.questionId && action.response === undefined) {
      delete entry.responses[action.questionId]
    }

    const context = Helpers.buildVisitContextWithResponses(
      state.intake.visit,
      state.intake.definition.allInstruments,
      state.intake.currentState.entries,
      state.intake.visit.patient,
    )

    const displayResults = collectDisplayLogicResults(
      view.screens,
      context,
      instrument.id,
      state,
      deleteHiddenResponsesMaximumInteractionCount,
    )

    yield put(
      Intake.actions.displayLogicEvaluated(displayResults, instrumentId),
    )
  }
}

function* handleDisplayLogicEvaluated() {
  yield takeEvery(Intake.ActionType.DisplayLogicEvaluated, clearHiddenResponses)
}

function* clearHiddenResponses(action: AppAction) {
  if (action.type === Intake.ActionType.DisplayLogicEvaluated) {
    const state: AppState = yield select((a) => a)
    const entry = state.intake.currentState.entries[action.instrumentId]

    for (const questionId of Object.keys(action.displayLogicResults)) {
      if (
        questionId &&
        !action.displayLogicResults[questionId] &&
        entry?.responses[questionId]
      ) {
        delete entry.responses[questionId]
        const request = Requests.deleteResponse(entry.id, questionId)
        yield call(request, state.config.apiLocation, state.auth.token)
      }
    }

    yield put(Intake.actions.entryUpdated(entry))
  }
}

function* handleAuthenticateResponses() {
  yield takeEvery(
    Intake.ActionType.AuthenticateResponses,
    startEntryAuthentication,
  )
}

function* startEntryAuthentication(action: AppAction) {
  if (action.type === Intake.ActionType.AuthenticateResponses) {
    yield put(Intake.actions.authenticateResponsesStarted())
    const state: AppState = yield select((a) => a)
    try {
      const patient = action.patient
      for (const entryId of Object.keys(state.intake.currentState.entries)) {
        const request = Requests.authenticateEntry(
          state.intake.currentState.entries[entryId].id,
          patient.id,
          action.signature,
        )
        yield call(request, state.config.apiLocation, state.auth.token)
      }
      yield put(Intake.actions.authenticateResponsesSuccess())
    } catch (e) {
      yield put(
        Intake.actions.authenticateResponsesFailure(
          'error during entry authentication',
        ),
      )
    }
  }
}

function* handleAuthenticationSuccess() {
  yield takeEvery(
    Intake.ActionType.AuthenticateResponsesSuccess,
    onAuthenticationSuccess,
  )
}

function* onAuthenticationSuccess(action: AppAction) {
  if (action.type === Intake.ActionType.AuthenticateResponsesSuccess) {
    yield put(Intake.actions.continue())
  }
}

function* handleWitnessResponses() {
  yield takeEvery(Intake.ActionType.WitnessResponses, startWitnessing)
}

function* startWitnessing(action: AppAction) {
  if (action.type === Intake.ActionType.WitnessResponses) {
    yield put(Intake.actions.witnessResponsesStarted())
    const state: AppState = yield select((a) => a)
    const request = UserRequests.verifyPIN(action.pin)
    const response: AxiosResponse<UserRequests.VerifyPINResponse> = yield call(
      request,
      state.config.apiLocation,
      state.auth.token,
    )
    if (response.status === 200) {
      if (response.data.status) {
        if (state.intake.definition.isSignatureRequired) {
          for (const entryId of Object.keys(
            state.intake.currentState.entries,
          )) {
            const request = Requests.witnessEntry(
              state.intake.currentState.entries[entryId].id,
              action.signature,
            )
            yield call(request, state.config.apiLocation, state.auth.token)
          }
        }
        yield put(
          Intake.actions.updateVisitStatus(
            state.intake.visit.id,
            VisitStage.Completed,
          ),
        )
        yield put(Intake.actions.witnessResponsesSuccess())
      } else {
        yield put(Intake.actions.witnessResponsesFailed('incorrect PIN'))
      }
    } else {
      yield put(Intake.actions.witnessResponsesFailed('Unable to verify PIN'))
    }
  }
}

function* handleWitnessResponsesSucceeded() {
  yield takeEvery(
    Intake.ActionType.WitnessResponsesSuccess,
    onWitnessResponsesSuccess,
  )
}

function* onWitnessResponsesSuccess(action: AppAction) {
  if (action.type === Intake.ActionType.WitnessResponsesSuccess) {
    const state: AppState = yield select((a) => a)
    yield put(
      Visits.actions.updateVisitState(state.intake.visit.id, {
        stage: VisitStage.Completed,
      }),
    )
    yield put(push(`/clinic/visit/${state.intake.visit.id}`))
  }
}

function* handleCloseSurvey() {
  yield takeEvery(Intake.ActionType.CloseSurvey, onCloseSurvey)
}

function* onCloseSurvey(action: AppAction) {
  if (action.type === Intake.ActionType.CloseSurvey) {
    const state: AppState = yield select((a) => a)
    yield put(
      Intake.actions.updateVisitStatus(
        state.intake.visit.id,
        VisitStage.Completed,
      ),
    )
    if (action.route) {
      yield put(push(action.route))
    }
  }
}

export const sagas = {
  spawnLoadTransitions,
  handleGetIntakeVisitEntries,
  handleSubmitResponses,
  handleContinue,
  handleGoBack,
  handleEvaluateDisplayLogic,
  handleDisplayLogicEvaluated,
  handleAuthenticateResponses,
  handleAuthenticationSuccess,
  handleWitnessResponses,
  handleWitnessResponsesSucceeded,
  handleUpdateVisitStatus,
  handleCloseSurvey,
  handleRestart,
}
